/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { TextField, Autocomplete, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

export const CssTextField = styled(TextField)(({ theme }) => ({
    '& label.Mui-focused': {
        color: theme.palette.cmpPrimary.main,
    },
    '& label.MuiFormLabel-filled': {
        // background: '#00709B',
        color: theme.palette.blue.main,
        padding: '0 2px',
    },
    '& label': {
        color: theme.palette.gray.graniteGray,
        fontSize: '14px !important',
        fontWeight: '400 !important',
        fontFamily: theme.typography.fontFamily,
    },
    '& .MuiInputBase-input': {
        color: theme.palette.gray.graniteGray,
        fontSize: '14px !important',
        fontWeight: '400 !important',
        fontFamily: theme.typography.fontFamily,
        background: '#FFFFFF',
        '&.Mui-disabled': {
            background: 'transparent',
        },
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            color: theme.palette.textColors.$color_text_tertiary,
            borderColor: theme.palette.border.$color_border_input,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.border.$color_border_input_hover,
        },
        '&.Mui-focused fieldset': {
            color: theme.palette.textColors.$color_text_primary,
            border: `2px solid ${theme.palette.border.$color_border_input_focus}`,
        },
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(14px, 10px) scale(1)',
    },
    '& .MuiInputLabel-shrink': {
        transform: 'translate(14px, -8px)',
        fontSize: '12px !important',
        fontWeight: '500 !important',
        fontFamily: theme.typography.fontFamily,
    },
    '& legend': {
        fontSize: '12px !important',
        fontWeight: '500 !important',
        fontFamily: theme.typography.fontFamily,
    },
    '& .Mui-disabled': {
        color: theme.palette.gray.graniteGray,
        backgroundColor: '#f4f4f4',
    },
}));

const Select = (props) => {
    const {
        options,
        label,
        value,
        handleOnSelect,
        disableClearable,
        disabled,
        isLoading,
        isRequired,
        bluredOnSelected,
        customValidate,
        placeholder,
        dataTestId,
        helperText,
        error,
        ...rest
    } = props;
    const validateInput = () => {
        if (customValidate) return customValidate;
        if (isRequired) {
            return value === '' || value === null || value === undefined ? 'validate' : '';
        }
        return '';
    };
    return (
        <Autocomplete
            data-testid={dataTestId}
            loading={isLoading}
            options={options}
            size="small"
            sx={{
                background: '#FFFFFF',
            }}
            fullWidth
            disablePortal
            disableClearable={disableClearable}
            disabled={disabled}
            onChange={(event, newValue, reason) => {
                handleOnSelect(newValue, reason);
            }}
            className="autocomplete-dropdown"
            value={value}
            blurOnSelect={bluredOnSelected}
            getOptionLabel={(option) => (option.label ? option.label.toString() : option.toString())}
            isOptionEqualToValue={(option, selectedValue) => option.label === selectedValue}
            renderInput={(params) => (
                <CssTextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        'data-testid': `${dataTestId}-input`,
                        endAdornment: (
                            <>
                                {isLoading && <CircularProgress color="inherit" size={16} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    className={validateInput()}
                />
            )}
            {...rest}
        />
    );
};

Select.propTypes = {
    options: PropTypes.any,
    label: PropTypes.any,
    value: PropTypes.any,
    handleOnSelect: PropTypes.func,
    disableClearable: PropTypes.bool,
    disabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isRequired: PropTypes.bool,
    bluredOnSelected: PropTypes.bool,
    customValidate: PropTypes.string,
    placeholder: PropTypes.string,
    dataTestId: PropTypes.string,
    helperText: PropTypes.node,
    error: PropTypes.bool,
};

Select.defaultProps = {
    options: [],
    label: '',
    value: undefined,
    handleOnSelect: undefined,
    disableClearable: true,
    disabled: false,
    isLoading: false,
    isRequired: false,
    bluredOnSelected: false,
    customValidate: undefined,
    placeholder: undefined,
    dataTestId: 'select-dropdown',
    helperText: '',
    error: false,
};

export default Select;
